import { useMentionCardsContext } from "@/containers/MentionCardsContext";
import { AudioMentionNew, EpisodeV4 } from "@/types/MentionCard";
import { Button } from "@components/ui/button";
import { Spinner } from "@components/ui/spinner";
import { AlertCircleIcon, PlayIcon, PauseIcon } from "lucide-react";
import {
  useMediaDispatch,
  useMediaSelector,
  MediaActionTypes,
} from "media-chrome/react/media-store";

export default function ActionButton({
  disabled,
  episodeId,
  mentions,
  onPlay,
}: {
  disabled: boolean;
  mentions: AudioMentionNew[];
  episodeId: EpisodeV4["id"];
  onPlay?: () => void;
}) {
  const { activeEpisodeId, setActiveEpisodeId } = useMentionCardsContext();

  const dispatch = useMediaDispatch();
  const mediaCurrentTime =
    useMediaSelector((state) => state.mediaCurrentTime) ?? 0;
  const mediaPaused = useMediaSelector((state) => state.mediaPaused);
  const mediaError = useMediaSelector((state) => state.mediaError);
  const mediaLoading = useMediaSelector((state) => state.mediaLoading);

  const lastMention = mentions[mentions.length - 1];
  const isPastLastMention =
    mediaCurrentTime > (lastMention.indices_metadata?.end_time ?? 0);

  const isActiveEpisode = activeEpisodeId === episodeId;

  return (
    <Button
      data-testid="play-link"
      disabled={disabled}
      onClick={() => {
        const type = mediaPaused
          ? MediaActionTypes.MEDIA_PLAY_REQUEST
          : MediaActionTypes.MEDIA_PAUSE_REQUEST;

        if (type === MediaActionTypes.MEDIA_PLAY_REQUEST) {
          if (!isActiveEpisode) {
            setActiveEpisodeId(episodeId);
          }
          onPlay?.();
        }

        setTimeout(() => {
          dispatch({ type });

          // Seek to first mention if at start or past last mention
          const shouldSeekToFirst =
            !Math.floor(mediaCurrentTime) || isPastLastMention;

          if (
            type === MediaActionTypes.MEDIA_PLAY_REQUEST &&
            shouldSeekToFirst
          ) {
            dispatch({
              type: MediaActionTypes.MEDIA_SEEK_REQUEST,
              detail: mentions[0].indices_metadata?.start_time ?? 0,
            });
          }
        }, 0);
      }}
      variant="ghost"
      className="rounded-full"
      size="icon-sm"
    >
      {mediaError && isActiveEpisode ? (
        <AlertCircleIcon />
      ) : mediaPaused ? (
        <PlayIcon className="fill-primary" />
      ) : mediaLoading ? (
        <Spinner />
      ) : (
        <PauseIcon className="fill-primary" />
      )}
      <span className="sr-only">{mediaPaused ? "Play" : "Pause"}</span>
    </Button>
  );
}
